import React, { useState, useEffect } from 'react';
import appInsightsService from '../services/appInsights';

const AppInsightsDiagnostic = () => {
  const [diagnosticResults, setDiagnosticResults] = useState([]);
  const [isRunningDiagnostic, setIsRunningDiagnostic] = useState(false);
  const [fixAttempted, setFixAttempted] = useState(false);

  const addResult = (test, success, details) => {
    setDiagnosticResults(prev => [...prev, { test, success, details, timestamp: new Date().toISOString() }]);
  };

  // Function to run the diagnostic tests
  const runDiagnostics = async () => {
    setIsRunningDiagnostic(true);
    setDiagnosticResults([]);
    
    try {
      // Check if App Insights is initialized
      const isInitialized = appInsightsService.initialized;
      addResult(
        'App Insights Initialized', 
        isInitialized, 
        isInitialized ? 'Application Insights is properly initialized' : 'Application Insights is NOT initialized'
      );
      
      // Check instrumentation key or connection string
      const instrumentationKey = process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY;
      const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;
      
      addResult(
        'Configuration Check', 
        !!(instrumentationKey || connectionString), 
        `InstrumentationKey: ${instrumentationKey ? 'Set' : 'Not Set'}, ConnectionString: ${connectionString ? 'Set' : 'Not Set'}`
      );
      
      // Check access to beacon API (used for sending telemetry)
      const hasBeaconAPI = typeof navigator.sendBeacon === 'function';
      addResult(
        'Browser Beacon API', 
        hasBeaconAPI,
        hasBeaconAPI ? 'Beacon API available' : 'Beacon API not available - this might cause issues with telemetry'
      );
        // Check CORS issues
      addResult(
        'CORS Configuration',
        true,
        'Pending - check browser console for CORS errors after test messages are sent'
      );
      
      // Check if we can directly test the Application Insights endpoint
      try {
        const testEndpoint = 'https://dc.services.visualstudio.com/v2/track';
        const testRequest = new Request(testEndpoint, {
          method: 'OPTIONS',
          mode: 'cors'
        });
        
        fetch(testRequest)
          .then(response => {
            addResult(
              'App Insights Endpoint Connectivity',
              response.ok,
              response.ok 
                ? 'Successfully connected to App Insights endpoint'
                : `Failed to connect to App Insights endpoint: ${response.status} ${response.statusText}`
            );
          })
          .catch(error => {
            addResult(
              'App Insights Endpoint Connectivity',
              false,
              `Error connecting to App Insights endpoint: ${error.message}`
            );
          });
      } catch (error) {
        addResult(
          'App Insights Endpoint Connectivity Test',
          false,
          `Could not test endpoint connectivity: ${error.message}`
        );
      }
      
      // Attempt to send test messages
      if (isInitialized) {
        try {
          // Track a test event
          appInsightsService.trackEvent('DiagnosticTest', { source: 'AppInsightsDiagnostic' });
          addResult('Test Event', true, 'Test event sent to Application Insights');
          
          // Track a test metric
          appInsightsService.trackMetric({
            name: 'DiagnosticTestMetric',
            average: 100,
            sampleCount: 1
          });
          addResult('Test Metric', true, 'Test metric sent to Application Insights');
          
          // Promise to wait for a short delay to allow the browser to send the data
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          addResult(
            'Check Browser Console', 
            true,
            'Check browser console for any errors related to dc.services.visualstudio.com'
          );
        } catch (error) {
          addResult('Send Test Telemetry', false, `Error: ${error.message}`);
        }
      } else {
        addResult(
          'Send Test Telemetry',
          false,
          'Skipped - Application Insights is not initialized'
        );
      }
    } catch (error) {
      addResult('Diagnostic Error', false, `Unexpected error: ${error.message}`);
    } finally {
      setIsRunningDiagnostic(false);
    }
  };
  
  // Function to attempt to fix common issues
  const attemptFix = () => {
    setFixAttempted(true);
    
    try {
      const appInsights = appInsightsService.appInsights;
      
      if (appInsights) {
        // Force the config to update
        appInsights.config.endpointUrl = "https://dc.services.visualstudio.com/v2/track";
        
        // Try setting a commonly missed option
        appInsights.config.disableFetchTracking = false;
        appInsights.config.enableCorsCorrelation = true;
        
        // Re-initialize the core sender
        if (appInsights.core && appInsights.core.getTransmissionControls) {
          const channels = appInsights.core.getTransmissionControls();
          if (channels && channels.length > 0) {
            try {
              channels[0]._sender.initialize(true);
              addResult('Fix - Re-initialize sender', true, 'Successfully re-initialized the telemetry sender');
            } catch (e) {
              addResult('Fix - Re-initialize sender', false, `Error: ${e.message}`);
            }
          }
        }
        
        // Force send pending data
        try {
          appInsights.flush();
          addResult('Fix - Flush pending data', true, 'Successfully flushed pending telemetry data');
        } catch (e) {
          addResult('Fix - Flush pending data', false, `Error: ${e.message}`);
        }
        
        addResult('Fix Attempt', true, 'Attempted fixes for common issues. Run diagnostics again to verify.');
      } else {
        addResult('Fix Attempt', false, 'Cannot attempt fix - Application Insights is not initialized');
      }
    } catch (error) {
      addResult('Fix Attempt', false, `Error during fix: ${error.message}`);
    }
  };

  useEffect(() => {
    // Run diagnostics on mount
    runDiagnostics();
  }, []);

  return (
    <div style={{ padding: '20px' }}>
      <h1>Application Insights Diagnostic</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={runDiagnostics}
          disabled={isRunningDiagnostic}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#0078d4', 
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isRunningDiagnostic ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          {isRunningDiagnostic ? 'Running...' : 'Run Diagnostics'}
        </button>
        
        <button 
          onClick={attemptFix}
          disabled={isRunningDiagnostic || fixAttempted}
          style={{ 
            padding: '8px 16px',
            backgroundColor: fixAttempted ? '#cccccc' : '#ff8c00',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (isRunningDiagnostic || fixAttempted) ? 'not-allowed' : 'pointer'
          }}
        >
          {fixAttempted ? 'Fix Attempted' : 'Attempt Fix'}
        </button>
      </div>
      
      <div style={{ marginTop: '20px' }}>
        <h2>Diagnostic Results</h2>
        {diagnosticResults.length === 0 ? (
          <p>No diagnostics run yet. Click the button above to test Application Insights.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Test</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Result</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Details</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {diagnosticResults.map((result, index) => (
                <tr key={index}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{result.test}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                    <span style={{ 
                      color: result.success ? 'green' : 'red',
                      fontWeight: 'bold'
                    }}>
                      {result.success ? '✓ Success' : '✗ Failed'}
                    </span>
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{result.details}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{result.timestamp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      <div style={{ marginTop: '20px' }}>
        <h2>400 Bad Request Troubleshooting</h2>
        <h3>Common Causes</h3>
        <ol>
          <li><strong>Invalid Instrumentation Key:</strong> The key may be incorrectly formatted or from a deleted resource</li>
          <li><strong>CORS Configuration:</strong> The Application Insights resource may not be configured to accept requests from your domain</li>
          <li><strong>Malformed Data:</strong> The data being sent may not conform to the expected format</li>
          <li><strong>Network or Proxy Issues:</strong> Corporate networks or proxies may be blocking telemetry</li>
          <li><strong>Content Security Policy:</strong> Your site's CSP may be blocking connections to dc.services.visualstudio.com</li>
        </ol>
        
        <h3>Solutions</h3>
        <ol>
          <li>Verify your instrumentation key in the Azure portal</li>
          <li>Use a connection string instead of just an instrumentation key</li>
          <li>Check the browser console for detailed error messages</li>
          <li>Ensure your app is allowed to make cross-origin requests to dc.services.visualstudio.com</li>
          <li>Try updating Application Insights SDK to the latest version</li>
        </ol>
      </div>
    </div>
  );
};

export default AppInsightsDiagnostic;
